import { z } from 'zod';

import { makeEndpoint } from '../common';

export enum SurveyQuestionType {
  SCALE_3 = 'SCALE_3',
  BINARY = 'BINARY',
}

export const SurveyQuestionTypeSchema = z.nativeEnum(SurveyQuestionType);

export const SurveyQuestionsSchema = z.object({
  surveyQuestionId: z.string().uuid(),
  surveyQuestionHistoryId: z.string().uuid(),
  active: z.boolean(),
  weight: z.number(),
  type: SurveyQuestionTypeSchema,
  label: z.string(),
  isAnalyticsActive: z.boolean().optional().nullable(),
  analyticsTitle: z.string().optional().nullable(),
  analyticsDescription: z.string().optional().nullable(),
});

export type ISurveyQuestionSchema = z.infer<typeof SurveyQuestionsSchema>;

export const SurveyQuestionWithAggregateAnswerSchema = z.object({
  surveyQuestionId: z.string(),
  active: z.boolean(),
  weight: z.number(),
  type: SurveyQuestionTypeSchema,
  label: z.string(),
  isAnalyticsActive: z.boolean().optional().nullable(),
  analyticsTitle: z.string().optional().nullable(),
  analyticsDescription: z.string().optional().nullable(),
  answers: z.array(
    z.object({
      answer: z.string().nullable(),
      count: z.number(),
    }),
  ),
});

export type SurveyQuestionWithAggregateAnswer = z.infer<typeof SurveyQuestionWithAggregateAnswerSchema>;

export const SurveyQuestionWithAggregateAnswerResponseSchema = z.array(SurveyQuestionWithAggregateAnswerSchema);
export type SurveyQuestionWithAggregateAnswerResponse = z.infer<typeof SurveyQuestionWithAggregateAnswerResponseSchema>;

export const GetSurveyQuestionsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `v1/survey-questions`,
  requestSchema: z.void(),
  responseSchema: z.array(SurveyQuestionsSchema),
});

export const PostSurveyQuestionsEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/survey-questions`,
  requestSchema: SurveyQuestionsSchema,
  responseSchema: z.void(),
});

export const DeleteSurveyQuestionsEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ surveyQuestionId }: { surveyQuestionId: string }) => `v1/survey-questions/${surveyQuestionId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const PatchSurveyQuestionsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ surveyQuestionId }: { surveyQuestionId: string }) => `v1/survey-questions/${surveyQuestionId}`,
  requestSchema: SurveyQuestionsSchema.pick({
    active: true,
    isAnalyticsActive: true,
    analyticsTitle: true,
    analyticsDescription: true,
  }),
  responseSchema: z.void(),
});

export const GetBrandSurveyQuestionsWithAggregateAnswerEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/survey-questions/aggregate-answers`,
  requestSchema: z.void(),
  responseSchema: SurveyQuestionWithAggregateAnswerResponseSchema,
});

export const GetOrganizationSurveyQuestionsWithAggregateAnswerEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId, currency }: { organizationId: string; currency: string }) =>
    `v1/organizations/${organizationId}/survey-questions/aggregate-answers/${currency}`,
  requestSchema: z.void(),
  responseSchema: SurveyQuestionWithAggregateAnswerResponseSchema,
});

export const GetOwnSurveyQuestionsWithAggregateAnswerEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ currency }: { currency: string }) => `v1/survey-questions/me/aggregate-answers/${currency}`,
  requestSchema: z.void(),
  responseSchema: SurveyQuestionWithAggregateAnswerResponseSchema,
});
